import React from 'react';
import dynamic from 'next/dynamic';
import { LoadMore } from '@components/Song/Audience/LoadMore';
import {
  DEFAULT_MOBILE_SEAT_COUNT,
  DEFAULT_TABLET_SEAT_COUNT,
  MOBILE_MAX_SEATS,
  TABLET_MAX_SEATS,
} from '@components/Song/Audience/shared/constants';
import { SeatingGrid } from '@components/Song/Audience/shared/styles';
import { MediaQuery } from '@context/mediaQuery';
import { AudienceSeat, EmptySeat } from './Seat';
import { useTopAudience } from './useTopAudience';

const VirtualizedTopAudience = dynamic(() =>
  import('@components/Song/Audience/Top/VirtualizedAudience').then(
    mod => mod.VirtualizedTopAudience,
  ),
);
/**
 * @dev When updating this component make sure to double check the expected states from the link below
 * https://www.figma.com/file/CfLYoS23bdpkE1z0X8cQXL/Song-Page-2.0?node-id=2839%3A238844&t=3UokwbmEJG9tNBnA-1
 */
export const TopAudience = ({ releaseId }: { releaseId: string }) => {
  const { tablet } = MediaQuery.useContainer();
  const [showRest, setShowRest] = React.useState(false);

  const { initialCollectors, restOfCollectors, loading, hasNextPage, fetchMore } = useTopAudience({
    releaseId,
    sort: {
      nftsCount: 'DESC',
    },
    paginationCount: tablet ? TABLET_MAX_SEATS : MOBILE_MAX_SEATS,
  });

  const collectorsData = React.useMemo(() => {
    if (!initialCollectors || initialCollectors.length === 0) return undefined;

    return initialCollectors;
  }, [initialCollectors]);

  const displayCondition = collectorsData
    ? collectorsData.length > 0 && initialCollectors != null
    : false;
  const paginateMoreCondition =
    collectorsData && collectorsData.length > 0 && displayCondition && hasNextPage;

  const showLoadMore = paginateMoreCondition || loading;

  const emptySeatCount = tablet ? DEFAULT_TABLET_SEAT_COUNT : DEFAULT_MOBILE_SEAT_COUNT;

  const collectorProfiles = React.useMemo(() => {
    if (!collectorsData) return;
    return collectorsData.map((collector, i) => (
      <AudienceSeat seatFrag={collector} index={i} key={`audience-seat-${collector.id}`} />
    ));
  }, [collectorsData]);

  return (
    <>
      <SeatingGrid>
        {!collectorsData
          ? Array.from({ length: emptySeatCount }).map((_, i) => (
              <EmptySeat index={i} key={`empty-seat-${i}`} />
            ))
          : collectorProfiles}
      </SeatingGrid>
      {/* Virtuouso Grid requires the components withing to have equally-sized items. Because of the design of the first 3 rows of our audience 
      - we are rending anything outside of the max seats in its own virtualized list */}
      {showRest && (
        <VirtualizedTopAudience otherCollectors={restOfCollectors} loadMore={fetchMore} />
      )}

      {showLoadMore ? (
        <LoadMore
          onClick={() => {
            if (!showRest) setShowRest(true);
            fetchMore();
          }}
          loading={loading}
          seating
        />
      ) : (
        !!collectorsData &&
        hasNextPage &&
        !showRest && <LoadMore onClick={() => setShowRest(prev => !prev)} loading={false} seating />
      )}
    </>
  );
};
